import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SegmentedControl,
  SkeletonTable,
  StatTile,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { ACCOUNTING_PATH, REVENUE_BASIS, readPnl, type PnlReport, type RevenueBasis } from '@/lib/finance';

const DAY_MS = 86_400_000;
const DEFAULT_WINDOW_DAYS = 365;

function ymd(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const moneyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const moneyFull = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const REVENUE_BASIS_OPTIONS: readonly { key: RevenueBasis; label: string }[] = [
  { key: REVENUE_BASIS.event, label: 'Event date' },
  { key: REVENUE_BASIS.signed, label: 'Signed date' },
];

/** The subtitle explains what the revenue column means under each basis. */
const BASIS_SUBTITLE: Readonly<Record<RevenueBasis, string>> = {
  [REVENUE_BASIS.event]:
    'Revenue from events that happened each month, set against the expenses paid that month.',
  [REVENUE_BASIS.signed]:
    'Revenue from contracts signed each month, set against the expenses paid that month.',
};

/** "2026-03" → its label "Mar 2026". */
function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The [from, to) day bounds of a "YYYY-MM" month, `to` exclusive. */
function monthWindow(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const start = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1));
  const end = new Date(Date.UTC(y ?? 1970, m ?? 1, 1));
  return { from: ymd(start), to: ymd(end) };
}

function money(value: number): string {
  return moneyCompact.format(value);
}

/** A right-aligned dollar cell; muted at zero. */
function AmountCell({ value }: { readonly value: number }): React.ReactElement {
  return (
    <td
      className={`whitespace-nowrap p-3 text-right text-portal-small ${value === 0 ? 'text-portal-muted' : 'text-portal-ink'}`}
      title={moneyFull.format(value)}
    >
      {value === 0 ? '—' : money(value)}
    </td>
  );
}

function NetCell({ value }: { readonly value: number }): React.ReactElement {
  const tone = value > 0 ? 'text-portal-success' : value < 0 ? 'text-portal-danger' : 'text-portal-muted';
  return (
    <td className={`whitespace-nowrap p-3 text-right text-portal-small font-bold ${tone}`} title={moneyFull.format(value)}>
      {value === 0 ? '—' : money(value)}
    </td>
  );
}

function PnlTable({ pnl, basis }: { readonly pnl: PnlReport; readonly basis: RevenueBasis }): React.ReactElement {
  const headers = [
    'Month',
    'Revenue',
    ...pnl.categories.map((c) => c.label),
    'Total exp.',
    'Net',
  ];
  // Everything except the Month column is right-aligned.
  const rightAligned = headers.map((_, i) => i).filter((i) => i > 0);
  const minWidth = 220 + (pnl.categories.length + 3) * 96;

  return (
    // A month-by-category matrix: on a phone it pans inside its card with the month pinned.
    <DataTable headers={headers} rightAlignedHeaders={rightAligned} minWidth={minWidth} stickyFirstColumn>
      {pnl.months.map((row) => {
        const win = monthWindow(row.month);
        return (
          <tr key={row.month} className="border-t border-portal-line align-middle">
            <td className="sticky left-0 whitespace-nowrap bg-white p-3 text-portal-small font-bold text-portal-ink">
              {monthLabel(row.month)}
            </td>
            <td
              className="whitespace-nowrap p-3 text-right text-portal-small font-bold"
              title={moneyFull.format(row.revenue)}
            >
              {row.revenue === 0 ? (
                <span className="text-portal-muted">—</span>
              ) : (
                <Link
                  to={`/analytics/events?from=${win.from}&to=${win.to}&outcome=signed&dateBy=${basis}`}
                  className="text-portal-success hover:underline"
                >
                  {money(row.revenue)}
                </Link>
              )}
            </td>
            {pnl.categories.map((c) => {
              const value = row.expenses[c.key] ?? 0;
              return (
                <td
                  key={c.key}
                  className="whitespace-nowrap p-3 text-right text-portal-small"
                  title={moneyFull.format(value)}
                >
                  {value === 0 ? (
                    <span className="text-portal-muted">—</span>
                  ) : (
                    <Link
                      to={`${ACCOUNTING_PATH.expenses}?pnl=${c.key}&from=${win.from}&to=${win.to}`}
                      className="text-brand hover:underline"
                    >
                      {money(value)}
                    </Link>
                  )}
                </td>
              );
            })}
            <AmountCell value={row.totalExpenses} />
            <NetCell value={row.net} />
          </tr>
        );
      })}
      {/* Totals */}
      <tr className="border-t-2 border-portal-line align-middle bg-portal-bg">
        <td className="sticky left-0 whitespace-nowrap bg-portal-bg p-3 text-portal-small font-bold text-portal-ink">Total</td>
        <AmountCell value={pnl.totals.revenue} />
        {pnl.categories.map((c) => (
          <AmountCell key={c.key} value={pnl.totals.expenses[c.key] ?? 0} />
        ))}
        <AmountCell value={pnl.totals.totalExpenses} />
        <NetCell value={pnl.totals.net} />
      </tr>
    </DataTable>
  );
}

/**
 * Profit & Loss: for each month, the revenue from events that happened that
 * month set against the expenses paid that month, grouped into P&L buckets.
 * Clicking a bucket amount drills to that month's line items on the Expenses
 * page. Admin-only (view_analytics), enforced by the API and the route guard.
 */
export function PnlPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const defaultTo = ymd(new Date());
  const defaultFrom = ymd(new Date(Date.now() - DEFAULT_WINDOW_DAYS * DAY_MS));
  const from = params.get('from') ?? defaultFrom;
  const to = params.get('to') ?? defaultTo;
  const basis: RevenueBasis =
    params.get('basis') === REVENUE_BASIS.signed ? REVENUE_BASIS.signed : REVENUE_BASIS.event;

  function setWindow(key: 'from' | 'to', value: string): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  function setBasis(next: RevenueBasis): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        // Event date is the default — keep it out of the URL.
        if (next === REVENUE_BASIS.event) nextParams.delete('basis');
        else nextParams.set('basis', next);
        return nextParams;
      },
      { replace: true },
    );
  }

  const pnl = useQuery({
    queryKey: ['finance-pnl', from, to, basis],
    queryFn: ({ signal }) => readPnl({ from, to, basis }, signal),
    placeholderData: keepPreviousData,
  });

  const totals = pnl.data?.totals;

  return (
    <section className="animate-portal-rise">
      <PageHeading eyebrow="ACCOUNTING" title="Profit & Loss" subtitle={BASIS_SUBTITLE[basis]} />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="pnl-from" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            From
          </label>
          <DatePicker id="pnl-from" value={from} placeholder="Start" onChange={(value) => setWindow('from', value)} />
        </div>
        <div>
          <label htmlFor="pnl-to" className="mb-1 block text-portal-micro font-bold text-portal-muted">
            To
          </label>
          <DatePicker id="pnl-to" value={to} placeholder="End" onChange={(value) => setWindow('to', value)} />
        </div>
        <div>
          <span className="mb-1 block text-portal-micro font-bold text-portal-muted">Revenue basis</span>
          <SegmentedControl<RevenueBasis>
            label="Revenue basis"
            value={basis}
            onChange={setBasis}
            options={REVENUE_BASIS_OPTIONS.map((option) => ({ key: option.key, label: option.label }))}
          />
        </div>
      </div>

      {totals ? (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          <StatTile label="Revenue" value={moneyFull.format(totals.revenue)} hint={`${from} → ${to}`} />
          <StatTile label="Expenses" value={moneyFull.format(totals.totalExpenses)} hint="paid in window" />
          <StatTile label="Net" value={moneyFull.format(totals.net)} hint="revenue − expenses" />
        </div>
      ) : null}

      {pnl.isError ? (
        <ErrorNotice title="P&L unavailable" message={errorMessage(pnl.error)} />
      ) : pnl.isPending ? (
        <SkeletonTable label="Loading the P&L…" />
      ) : pnl.data.months.length === 0 ? (
        <Panel>
          <EmptyState
            title="No P&L for this window"
            message="No events or expenses fall in these dates. Widen the range."
          />
        </Panel>
      ) : (
        <PnlTable pnl={pnl.data} basis={basis} />
      )}
    </section>
  );
}
